import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import { channelSettings } from '~publish/legacy/routes'

import {
  LINKEDIN_CAPABILITIES_SCROLL_HASH,
  LINKEDIN_CREDIBILITY_SIGNALS_SCOPE,
  PAGE_SECTION_TITLE,
  PROFILE_SECTION_TITLE,
} from './config'
import type {
  ChannelCapabilityKey,
  LinkedInProfile,
  LinkedInVerifiedCapability,
} from './config'
import {
  trackLinkedInCapabilitiesRefreshClicked,
  trackLinkedInCapabilitiesSettingsViewed,
  trackLinkedInCapabilitiesSyncToggleClicked,
  trackLinkedInCapabilitiesTableToggled,
  trackLinkedInCapabilitySelected,
} from './tracking'

export const GetLinkedInVerifiedSkills = graphql(/* GraphQL */ `
  query GetLinkedInVerifiedSkills($channelId: ChannelId!) {
    channel(input: { id: $channelId }) {
      id
      externalLink
      isLocked
      scopes
      capabilities {
        isEnabled
        lastCheckedAt
        capabilities {
          capabilityKey
          name
          description
          isEarned
          isDisplayedOnPlatform
        }
      }
    }
  }
`)

export const ToggleLinkedInVerifiedSkillSynchronization =
  graphql(/* GraphQL */ `
    mutation ToggleLinkedInVerifiedSkillSynchronization(
      $input: ToggleChannelCapabilitySynchronizationInput!
    ) {
      toggleChannelCapabilitySynchronization(input: $input) {
        __typename
        ... on EmptySuccess {
          _empty
        }
        ... on VoidMutationError {
          message
        }
      }
    }
  `)

export const CheckLinkedInCapabilities = graphql(/* GraphQL */ `
  mutation CheckLinkedInCapabilities($input: CheckChannelCapabilitiesInput!) {
    checkChannelCapabilities(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on LimitReachedError {
        message
      }
    }
  }
`)

export const SetLinkedInCapabilityToDisplayOnNetwork = graphql(/* GraphQL */ `
  mutation SetLinkedInCapabilityToDisplayOnNetwork(
    $input: SetChannelCapabilityToDisplayOnNetworkInput!
  ) {
    setChannelCapabilityToDisplayOnNetwork(input: $input) {
      __typename
      ... on EmptySuccess {
        _empty
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UseLinkedInCapabilitiesSettingsResult = {
  capabilities: LinkedInVerifiedCapability[]
  checkCapabilitiesErrorMessage?: string
  chooseCapabilityPopoverCapabilityKey?: ChannelCapabilityKey
  displayedSelectedCapabilityKey?: ChannelCapabilityKey
  error?: ErrorLike
  hasLoadedCapabilities: boolean
  isChannelLocked: boolean
  isCheckingCapabilities: boolean
  isChooseCapabilityPopoverOpen: boolean
  isInitialLoading: boolean
  isLinkedInPage: boolean
  isRefreshPopoverOpen: boolean
  isSwitchDisabled: boolean
  isSwitchOn: boolean
  lastCheckedAt: string | null
  linkedInProfileUrl: string | null
  sectionTitle: string
  shouldFetchCapabilities: boolean
  shouldShowChooseCapabilityPopover: boolean
  shouldShowRefreshPopover: boolean
  handleCapabilitySelect: (capabilityKey: ChannelCapabilityKey) => void
  handleCheckCapabilities: () => void
  handleChooseCapabilityPopoverOpenChange: (isOpen: boolean) => void
  handleRefreshChannel: () => void
  handleSwitchChange: (checked: boolean) => void
  handleTableToggle: (expanded: boolean) => void
  setIsRefreshPopoverOpen: (isOpen: boolean) => void
}

const getProfileName = (profile: LinkedInProfile): string =>
  profile.displayName ??
  profile.serviceUsername ??
  profile.service_username ??
  profile.username ??
  profile.handle ??
  ''

const getCapabilitiesDestinationUrl = (channelId: string): string => {
  const destinationUrl = new URL(
    channelSettings.getRoute({ channelId, tab: 'general' }),
    window.location.origin,
  )
  destinationUrl.hash = LINKEDIN_CAPABILITIES_SCROLL_HASH

  return destinationUrl.toString()
}

const getLinkedInProfileUrl = (profile: LinkedInProfile): string | null => {
  const profileIdentifier =
    profile.serviceUsername ??
    profile.service_username ??
    profile.username ??
    profile.handle
  const normalizedProfileIdentifier = profileIdentifier
    ?.trim()
    .replace(/^@/, '')

  return normalizedProfileIdentifier
    ? `https://www.linkedin.com/in/${encodeURIComponent(normalizedProfileIdentifier)}`
    : null
}

const useLinkedInCapabilitiesSettings = (
  profile: LinkedInProfile,
): UseLinkedInCapabilitiesSettingsResult => {
  const [isRefreshPopoverOpen, setIsRefreshPopoverOpen] = useState(false)
  const [isChooseCapabilityPopoverOpen, setIsChooseCapabilityPopoverOpen] =
    useState(false)
  const [
    chooseCapabilityPopoverCapabilityKey,
    setChooseCapabilityPopoverCapabilityKey,
  ] = useState<ChannelCapabilityKey | undefined>()
  const [pendingSelectedCapabilityKey, setPendingSelectedCapabilityKey] =
    useState<ChannelCapabilityKey | undefined>()
  const [checkCapabilitiesErrorMessage, setCheckCapabilitiesErrorMessage] =
    useState<string | undefined>()
  const refreshChannelConnection = useRefreshChannelConnection()
  const contextOrganizationId = useOrganizationId()
  const organizationId = contextOrganizationId ?? profile.organizationId
  const lastTrackedSettingsViewedChannelId = useRef<string | null>(null)
  const isLinkedInPage = profile.service_type === 'page'
  const shouldFetchCapabilities =
    profile.service === 'linkedin' && !isLinkedInPage
  const sectionTitle = isLinkedInPage
    ? PAGE_SECTION_TITLE
    : PROFILE_SECTION_TITLE

  const { data, error, loading } = useQuery(GetLinkedInVerifiedSkills, {
    variables: { channelId: profile.id },
    skip: !shouldFetchCapabilities,
    fetchPolicy: 'cache-and-network',
  })
  const [toggleSynchronization, { loading: isTogglingSynchronization }] =
    useMutation(ToggleLinkedInVerifiedSkillSynchronization, {
      refetchQueries: [
        {
          query: GetLinkedInVerifiedSkills,
          variables: { channelId: profile.id },
        },
      ],
      awaitRefetchQueries: true,
    })
  const [setCapabilityToDisplay] = useMutation(
    SetLinkedInCapabilityToDisplayOnNetwork,
    {
      onError: () => {
        setPendingSelectedCapabilityKey(undefined)
      },
      refetchQueries: [
        {
          query: GetLinkedInVerifiedSkills,
          variables: { channelId: profile.id },
        },
      ],
      awaitRefetchQueries: true,
    },
  )
  const [checkCapabilities, { loading: isCheckingCapabilities }] = useMutation(
    CheckLinkedInCapabilities,
    {
      onCompleted: (result) => {
        if (
          result.checkChannelCapabilities.__typename === 'LimitReachedError'
        ) {
          setCheckCapabilitiesErrorMessage(
            result.checkChannelCapabilities.message,
          )
        }
      },
      onError: () => {
        setCheckCapabilitiesErrorMessage(
          'Unable to check capabilities right now. Please try again later.',
        )
      },
      refetchQueries: (result) =>
        result.data?.checkChannelCapabilities.__typename === 'EmptySuccess'
          ? [
              {
                query: GetLinkedInVerifiedSkills,
                variables: { channelId: profile.id },
              },
            ]
          : [],
      awaitRefetchQueries: true,
    },
  )

  const capabilities = data?.channel.capabilities.capabilities ?? []
  const earnedCount = capabilities.filter(
    (capability) => capability.isEarned,
  ).length
  const selectedCapability = capabilities.find(
    (capability) => capability.isDisplayedOnPlatform,
  )
  const selectedCapabilityKey = selectedCapability?.capabilityKey
  const displayedSelectedCapabilityKey =
    pendingSelectedCapabilityKey ?? selectedCapabilityKey
  const hasLoadedCapabilities = Boolean(data)
  const isInitialLoading = loading && !hasLoadedCapabilities
  const isProfileLocked = profile.isLocked === true || profile.disabled === true
  const isChannelLocked = data?.channel.isLocked === true || isProfileLocked
  const hasCredibilitySignalsScope =
    data?.channel.scopes.includes(LINKEDIN_CREDIBILITY_SIGNALS_SCOPE) ?? false
  const isCapabilitySynchronizationEnabled =
    data?.channel.capabilities.isEnabled ?? false
  const lastCheckedAt = data?.channel.capabilities.lastCheckedAt ?? null
  const isSwitchOn =
    !isChannelLocked &&
    hasCredibilitySignalsScope &&
    isCapabilitySynchronizationEnabled
  const isSwitchDisabled =
    isChannelLocked ||
    isLinkedInPage ||
    isInitialLoading ||
    isTogglingSynchronization ||
    Boolean(error)
  const shouldShowRefreshPopover =
    shouldFetchCapabilities &&
    hasLoadedCapabilities &&
    !isChannelLocked &&
    !hasCredibilitySignalsScope &&
    !error
  const shouldShowChooseCapabilityPopover =
    shouldFetchCapabilities &&
    hasLoadedCapabilities &&
    !isChannelLocked &&
    hasCredibilitySignalsScope &&
    Boolean(chooseCapabilityPopoverCapabilityKey) &&
    !error
  const linkedInProfileUrl =
    data?.channel.externalLink ?? getLinkedInProfileUrl(profile)

  useEffect(() => {
    if (
      pendingSelectedCapabilityKey !== undefined &&
      selectedCapabilityKey === pendingSelectedCapabilityKey
    ) {
      setPendingSelectedCapabilityKey(undefined)
    }
  }, [pendingSelectedCapabilityKey, selectedCapabilityKey])

  useEffect(() => {
    if (
      profile.service !== 'linkedin' ||
      !organizationId ||
      lastTrackedSettingsViewedChannelId.current === profile.id
    ) {
      return
    }

    if (isLinkedInPage) {
      trackLinkedInCapabilitiesSettingsViewed({
        organizationId,
        channelId: profile.id,
        channelType: 'page',
      })
      lastTrackedSettingsViewedChannelId.current = profile.id
      return
    }

    if (!hasLoadedCapabilities || error) {
      return
    }

    trackLinkedInCapabilitiesSettingsViewed({
      organizationId,
      channelId: profile.id,
      channelType: 'profile',
      hasRequiredScope: hasCredibilitySignalsScope,
      isSyncEnabled: isCapabilitySynchronizationEnabled,
      earnedCount,
      totalCount: capabilities.length,
      selectedCapabilityKey,
    })
    lastTrackedSettingsViewedChannelId.current = profile.id
  }, [
    capabilities.length,
    earnedCount,
    error,
    hasCredibilitySignalsScope,
    hasLoadedCapabilities,
    isCapabilitySynchronizationEnabled,
    isLinkedInPage,
    organizationId,
    profile.id,
    profile.service,
    selectedCapabilityKey,
  ])

  const handleSwitchChange = (checked: boolean): void => {
    trackLinkedInCapabilitiesSyncToggleClicked({
      organizationId,
      channelId: profile.id,
      targetState: checked ? 'enabled' : 'disabled',
      currentState: isSwitchOn ? 'enabled' : 'disabled',
      hasRequiredScope: hasCredibilitySignalsScope,
      hasSelectedCapability: Boolean(displayedSelectedCapabilityKey),
      selectedCapabilityKey: displayedSelectedCapabilityKey,
    })

    if (isChannelLocked) {
      return
    }

    // When the channel is missing the required scope, clicking the switch
    // surfaces the refresh popover (to grant permissions) instead of toggling
    // synchronization.
    if (!hasCredibilitySignalsScope) {
      setIsRefreshPopoverOpen(true)
      return
    }

    void toggleSynchronization({
      variables: {
        input: {
          channelId: profile.id,
          toggle: checked,
        },
      },
    })
  }

  const handleCheckCapabilities = (): void => {
    if (isChannelLocked || isCheckingCapabilities) {
      return
    }

    setCheckCapabilitiesErrorMessage(undefined)

    void checkCapabilities({
      variables: { input: { channelId: profile.id } },
    })
  }

  const handleCapabilitySelect = (
    capabilityKey: ChannelCapabilityKey,
  ): void => {
    trackLinkedInCapabilitySelected({
      organizationId,
      channelId: profile.id,
      capabilityKey,
      previousCapabilityKey: selectedCapabilityKey,
      isFirstSelection: !selectedCapabilityKey,
      isSyncEnabled: isCapabilitySynchronizationEnabled,
    })

    if (!displayedSelectedCapabilityKey) {
      setChooseCapabilityPopoverCapabilityKey(capabilityKey)
      setIsChooseCapabilityPopoverOpen(true)
    }

    setPendingSelectedCapabilityKey(capabilityKey)

    void setCapabilityToDisplay({
      variables: {
        input: {
          channelId: profile.id,
          capabilityKey,
        },
      },
    })
  }

  const handleChooseCapabilityPopoverOpenChange = (isOpen: boolean): void => {
    setIsChooseCapabilityPopoverOpen(isOpen)

    if (!isOpen) {
      setChooseCapabilityPopoverCapabilityKey(undefined)
    }
  }

  const handleRefreshChannel = (): void => {
    setIsRefreshPopoverOpen(false)
    trackLinkedInCapabilitiesRefreshClicked({
      organizationId,
      channelId: profile.id,
      cta: 'publish-linkedin-capabilities-refresh-channel',
    })
    refreshChannelConnection({
      channel: {
        apiVersion: profile.api_version,
        id: profile.id,
        name: getProfileName(profile),
        service: 'linkedin',
      },
      cta: 'publish-linkedin-capabilities-refresh-channel',
      destinationUrl: getCapabilitiesDestinationUrl(profile.id),
    })
  }

  const handleTableToggle = (expanded: boolean): void => {
    trackLinkedInCapabilitiesTableToggled({
      organizationId,
      channelId: profile.id,
      expanded,
      earnedCount,
      totalCount: capabilities.length,
    })
  }

  return {
    capabilities,
    checkCapabilitiesErrorMessage,
    chooseCapabilityPopoverCapabilityKey,
    displayedSelectedCapabilityKey,
    error,
    hasLoadedCapabilities,
    isChannelLocked,
    isCheckingCapabilities,
    isChooseCapabilityPopoverOpen,
    isInitialLoading,
    isLinkedInPage,
    isRefreshPopoverOpen,
    isSwitchDisabled,
    isSwitchOn,
    lastCheckedAt,
    linkedInProfileUrl,
    sectionTitle,
    shouldFetchCapabilities,
    shouldShowChooseCapabilityPopover,
    shouldShowRefreshPopover,
    handleCapabilitySelect,
    handleCheckCapabilities,
    handleChooseCapabilityPopoverOpenChange,
    handleRefreshChannel,
    handleSwitchChange,
    handleTableToggle,
    setIsRefreshPopoverOpen,
  }
}

export { useLinkedInCapabilitiesSettings }
